// Web: plays the lip-sync renderer's MediaStream in a hidden <video> (its audio still plays)
// and draws each frame into a <canvas> with a chroma key (green 0,177,64) so she stands on the hall.
import React, { useEffect, useRef } from 'react';
import type { ViewStyle } from 'react-native';
import { chromaKey } from './chromaKey';

const CW = 540;
const CH = 675; // 4:5, matches the source footage

export function AvatarVideo({ stream, style, onFirstFrame }: { stream: MediaStream; style: ViewStyle; onFirstFrame?: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const firstRef = useRef(onFirstFrame);
  firstRef.current = onFirstFrame;

  useEffect(() => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    v.srcObject = stream;
    void v.play().catch(() => {});
    const ctx = c.getContext('2d', { willReadFrequently: true });
    let raf = 0;
    let stopped = false;
    let announced = false;
    let lastT = -1;
    const draw = () => {
      if (stopped) return;
      raf = requestAnimationFrame(draw);
      if (!ctx || v.readyState < 2 || v.videoWidth === 0 || v.currentTime === lastT) return;
      lastT = v.currentTime;
      ctx.drawImage(v, 0, 0, CW, CH);
      const img = ctx.getImageData(0, 0, CW, CH);
      const d = img.data;
      chromaKey(d);
      ctx.putImageData(img, 0, 0);
      if (!announced) {
        announced = true;
        firstRef.current?.();
      }
    };
    raf = requestAnimationFrame(draw);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      v.srcObject = null;
    };
  }, [stream]);

  const s = style as Record<string, number | string | undefined>;
  return React.createElement(
    React.Fragment,
    null,
    React.createElement('video', {
      ref: videoRef,
      autoPlay: true,
      playsInline: true,
      muted: false,
      style: { position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' },
    }),
    React.createElement('canvas', {
      ref: canvasRef,
      width: CW,
      height: CH,
      style: { position: 'absolute', left: s.left, top: s.top, width: s.width, height: s.height, pointerEvents: 'none' },
    }),
  );
}
